import { fitWithin } from "./photo";

// Logo and cover photo of "Aspect și personalizare". The prototype's checks (`readImg`: PNG, JPG, WebP or SVG;
// logo up to 4 MB, cover up to 8 MB), then the image is re-encoded in the browser like product photos (DEC-041):
// the server stores only JPEG/PNG/WebP, so an SVG logo is drawn into a bitmap here and never uploaded as SVG
// (an SVG file can carry script). Re-encoding also drops EXIF metadata.

export type BrandImageKind = "logo" | "cover";

export const BRAND_ACCEPT: Record<BrandImageKind, string> = {
  logo: "image/png,image/jpeg,image/webp,image/svg+xml",
  cover: "image/png,image/jpeg,image/webp",
};

const MAX_MB: Record<BrandImageKind, number> = { logo: 4, cover: 8 };
/** Longest side after re-encoding: the logo is shown at 64 px (52 px in the preview), the cover across the page. */
const MAX_SIDE: Record<BrandImageKind, number> = { logo: 512, cover: 1600 };

/** Prototype messages. */
export function brandFileError(file: File, kind: BrandImageKind): string | null {
  if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type))
    return "Format neacceptat. Folosește PNG, JPG sau WebP.";
  if (file.size > MAX_MB[kind] * 1048576) return `Fișierul depășește ${MAX_MB[kind]} MB.`;
  return null;
}

const UNREADABLE = "Imaginea nu a putut fi citită. Încearcă alt fișier.";

async function decode(
  file: File,
): Promise<{ source: CanvasImageSource; w: number; h: number; done: () => void }> {
  if (file.type !== "image/svg+xml") {
    try {
      const b = await createImageBitmap(file);
      return { source: b, w: b.width, h: b.height, done: () => b.close() };
    } catch {
      throw new Error(UNREADABLE);
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    // A vector logo has no pixel size of its own: draw it at the target size.
    return {
      source: img,
      w: img.naturalWidth || 1,
      h: img.naturalHeight || 1,
      done: () => URL.revokeObjectURL(url),
    };
  } catch {
    URL.revokeObjectURL(url);
    throw new Error(UNREADABLE);
  }
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

/** Decodes, downsizes and re-encodes (WebP, PNG where WebP encoding is missing: keeps a logo's transparency). */
export async function prepareBrandImage(file: File, kind: BrandImageKind): Promise<Blob> {
  const img = await decode(file);
  try {
    const max = MAX_SIDE[kind];
    const vector = file.type === "image/svg+xml";
    const scale = vector ? max / Math.max(img.w, img.h) : 1;
    const size = fitWithin(img.w * scale, img.h * scale, max);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Imaginea nu a putut fi pregătită. Încearcă din nou.");
    ctx.drawImage(img.source, 0, 0, size.width, size.height);
    let out: Blob | null = null;
    try {
      out = await toBlob(canvas, "image/webp", 0.85);
      if (out?.type !== "image/webp") out = await toBlob(canvas, "image/png");
    } catch {
      out = null;
    }
    if (!out) throw new Error("Imaginea nu a putut fi pregătită. Încearcă din nou.");
    return out;
  } finally {
    img.done();
  }
}
